import React, { useEffect, useRef, memo, useCallback } from 'react';

import { utilsLog } from '../../utils/logUtils';

import { LinkHighlightButton } from '../HighlightButton/HighlightButton';
import styles from './ArrangementCard.module.css';

// Helper function to get the correct YouTube embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  // Extracts video ID from various YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = (match && match[2].length === 11) ? match[2] : null;

  if (!videoId) return '';

  // Construct the URL with parameters to hide controls and branding
  const params = new URLSearchParams({
    autoplay: 1,
    controls: 0,
    showinfo: 0,
    modestbranding: 1,
    loop: 1,
    rel: 0,
    iv_load_policy: 3,
    playlist: videoId, // Required for loop to work
    enablejsapi: 1 // Enable the JavaScript API
  });

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

// For other pages using the card dimensions
export const ImageFrame = ({ children }) => {
  return <div className={styles.imageContainer}>{children}</div>
}

// We use memo() here because each item is one of many in a grid/list in Arrangements.jsx
// Every time state in Arrangements.jsx changes, which can happen even during scrolling,
// we don't want to re-render every ArrangementCard component
const ArrangementCard = memo(({ positionNumber, src, alt, item, type, pauseAllMedia, listName }) => {
  const [isImageLoaded, setImageLoaded] = React.useState(false);
  const [flipped, setFlipped] = React.useState(false);
  const [audioError, setAudioError] = React.useState(false);
  const audioRef = useRef(null);
  const iframeRef = useRef(null);

  const handleActionButtonClick = async (buttonType) => {
    const { logAnalyticsEvent } = await import('../../utils/analyticsUtils');
    logAnalyticsEvent('item_button_click', {
      item_title: item.title,
      button_type: buttonType
    });
  };

  const handleItemClick = useCallback(async () => {
    const { logAnalyticsEvent } = await import('../../utils/analyticsUtils');
    logAnalyticsEvent('item_click', {
      arrangement_id: item.id,
      arrangement_title: item.title
    });
  }, [item.id, item.title]);

  const pauseAllAndPlayCurrent = useCallback(() => {
    // Pause all media EXCEPT this card's audio
    // Each card will only have ONE of audio or iframe ref
    pauseAllMedia(audioRef.current, iframeRef.current);
    if (audioRef.current) {
      audioRef.current.play();
    } else if (iframeRef.current) {
      // Probably doesn't matter because iframe autoplays on DOM insertion
      iframeRef.current.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      );
    }
  }, [pauseAllMedia]);

  // pause others and play audio when flipped
  useEffect(() => {
    try {
      // audio card handling
      if (flipped && audioRef.current) {
        pauseAllAndPlayCurrent();
      } else if (!flipped && audioRef.current) {
        audioRef.current.pause();
      }

      // iframe handling
      if (flipped && iframeRef.current) {
        pauseAllAndPlayCurrent();
      } else if (!flipped && iframeRef.current) {
        iframeRef.current.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      }
    } catch (error) {
      utilsLog(error, 'error');
    }

    return () => pauseAllMedia();
  }, [flipped, pauseAllMedia, pauseAllAndPlayCurrent]);

  return (
    <div className={styles.imageContainer}>
      <button
        className={styles.clickableBackground}
        type="button"
        aria-label={`Play ${item.title}}`}
        onClick={() => setFlipped(!flipped)}
      />

      <div className={styles.cardContentClickThrough} hidden={flipped} onClick={handleItemClick}>
        <img
          className={type === "arrangement" ? styles.arrangementImg : styles.transcriptionImg}
          fetchpriority={listName === "A Cappella/Choir" && positionNumber === 0 ? 'high' : 'low'}
          loading={listName === "A Cappella/Choir" && positionNumber < 4 ? 'eager' : 'lazy'}
          src={src}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onError={() => utilsLog(`Failed to load image ${alt}`, 'error')}
          width={330}
          height={330}
        />

        {item.specialtext && item.showspecialtext &&
          <div className={styles.topOverlay}>
            <div><span className={styles.topOverlayText}>{item.specialtext}</span></div>
          </div>
        }

        {item.commissionedby && item.showcommissiontext &&
          <div className={styles.topOverlay}>
            <div><span style={{ marginRight: 5 }}>🖋️</span><span className={styles.topOverlayText}>Commissioned by {item.commissionedby}</span></div>
          </div>
        }

        <div className={styles.middleOverlay}>
          <div className={styles.playIconContainer}>
            <svg id="play-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-play-icon lucide-play ${styles.playIcon}`}><path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" /></svg>
          </div>
        </div>


        <div className={styles.bottomOverlay}>
          <h3 className={styles.songTitle}>{item.title}</h3>
          <div><em><span className={styles.subscriptText}>by </span><span className={styles.byText}>{item.by}</span></em></div>
          <div className={styles.partsText}>{item.parts}</div>
        </div>
      </div>

      {flipped && (
        <div className={styles.cardContentClickThrough}>
          <div className={styles.overlay}>
            <button className={styles.exitContainer} aria-label="Close" onClick={() => setFlipped(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-x-icon lucide-x ${styles.exitIcon}`} aria-hidden="true"><path d="m6 6 12 12m-12 0 12-12" /></svg>
            </button>

            {type === "arrangement" && !audioError &&
              <div className={styles.htmlAudioContainer}>
                <audio
                  ref={audioRef}
                  className={styles.audio}
                  autoPlay
                  controls
                  controlsList="nodownload noplaybackrate nomute notimeremaining"
                  onPlay={pauseAllAndPlayCurrent}
                  onError={() => {
                    utilsLog('error', `Failed to load audio: ${item.mp3}`);
                    setAudioError(true);
                  }}
                >
                  <source src={`/static/products/${item.id}/${item.id}.mp3`} type="audio/mp3" />
                  There was an error playing or loading the audio.
                </audio>
              </div>
            }

            {type === "transcription" && item.youtube && flipped &&
              <iframe
                ref={iframeRef}
                title={item.title}
                width="100%"
                height="100%"
                src={getYouTubeEmbedUrl(item.youtube)}
                allow="accelerometer; autoplay; encrypted-media;"
                style={{ border: 'none' }}
              />
              // TODO: use YouTube API to track native iframe player events, 
              //   like clicking play on the player controls of a paused video iframe
              //   this way, we can make sure other media ALSO stops when explicitly
              //   clicking iframe video's play button
            }

            <div className={styles.actionButtonsContainer}>
              <LinkHighlightButton
                to={`/store/${item.id}`}
                onClick={() => handleActionButtonClick('more_info')}
              >
                <span className={styles.buttonText}>MORE INFO</span>
              </LinkHighlightButton>

              <LinkHighlightButton
                to={`/store/${item.id || item.title.toLowerCase().replace(/\s+/g, '-')}?checkout=true`}
                onClick={() => handleActionButtonClick('purchase')}
              >
                <span className={styles.buttonText}>PURCHASE</span>
              </LinkHighlightButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ArrangementCard.displayName = 'ArrangementCard';

export { ArrangementCard };